import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DialogHeader from './DialogHeader';
import './../assets/styles/TableModalStyles.css';

export default function PasswordChangeDialog({
  open,
  onClose,
  onSubmit,
  title = 'Change Password',
  subtitle = 'Update your login credentials securely',
  submitLabel = 'Update Password',
  requireCurrentPassword = true,
  loading = false
}) {
  const [values, setValues] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  useEffect(() => {
    if (!open) {
      setValues({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setErrors({});
      setShow({ currentPassword: false, newPassword: false, confirmPassword: false });
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    const next = { ...values, [field]: e.target.value };
    setValues(next);

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validatePasswordFields = ({ currentPassword, newPassword, confirmPassword }, options = {}) => {
    const errors = {};
    const requireCurrent = options.requireCurrentPassword ?? true;

    if (requireCurrent && !currentPassword?.trim()) {
      errors.currentPassword = 'Current password is required';
    }

    if (!newPassword?.trim()) {
      errors.newPassword = 'New password is required';
    } else {
      const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&^()_+\-=[\]{};':"\\|,.<>/?]).{6,}$/;
      if (!passwordRegex.test(newPassword)) {
        errors.newPassword = 'Minimum 6 characters with 1 letter, 1 number and 1 special character';
      }
    }

    if (!confirmPassword?.trim()) {
      errors.confirmPassword = 'Confirm password is required';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  const handleSubmit = async () => {
    const nextErrors = validatePasswordFields(values, { requireCurrentPassword });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    await onSubmit(values);
  };

  const field = (key, label) => (
    <TextField
      fullWidth
      label={label}
      type={show[key] ? 'text' : 'password'}
      value={values[key]}
      onChange={handleChange(key)}
      error={!!errors[key]}
      helperText={errors[key]}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => setShow((p) => ({ ...p, [key]: !p[key] }))} edge="end">
              {show[key] ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );

  return (
    <Dialog open={open} className="custom-dialog" maxWidth="sm" fullWidth>
      <DialogHeader title="Change Password" onClose={onClose} />

      <DialogContent className="custom-dialog-content">
        <Stack spacing={2.2}>
          {requireCurrentPassword && field('currentPassword', 'Current Password')}
          {field('newPassword', 'New Password')}
          {field('confirmPassword', 'Confirm New Password')}
        </Stack>
      </DialogContent>

      <DialogActions className="custom-dialog-actions">
        <Button className="modal-action-btn modal-primary-btn" startIcon={<LockOutlinedIcon />} onClick={handleSubmit} disabled={loading}>
          {submitLabel}
        </Button>
        <Button className="modal-action-btn modal-danger-btn" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
